"use client";

import { useEffect, useState } from "react";

export type CaseStudyTocItem = {
  id: string;
  label: string;
};

export type CaseStudyTocProps = {
  items?: CaseStudyTocItem[];
};

const getScrollOffset = () =>
  typeof window !== "undefined"
    ? window.innerHeight * 0.1
    : 0;

const scrollToSection = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return;

  const top =
    el.getBoundingClientRect().top + window.scrollY - getScrollOffset();

  window.scrollTo({
    top: Math.max(0, top),
    behavior: "smooth",
  });
};

const CaseStudyToc = ({ items = [] }: CaseStudyTocProps) => {
  const [activeId, setActiveId] = useState(items[0]?.id ?? "");

  useEffect(() => {
    if (items.length === 0) return;

    const elements = items
      .map((item) => document.getElementById(item.id))
      .filter(Boolean) as HTMLElement[];

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]?.target?.id) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        rootMargin: `-${Math.round(getScrollOffset())}px 0px -55% 0px`,
        threshold: [0.1, 0.35, 0.6],
      }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  useEffect(() => {
    if (items.length === 0) return;
    const hash = window.location.hash.replace(/^#/, "");
    if (hash && items.some((item) => item.id === hash)) {
      // Wait a tick so layout is ready
      requestAnimationFrame(() => scrollToSection(hash));
    }
  }, [items]);

  if (items.length === 0) return null;

  return (
    <aside className="cs-toc" data-cs-section="toc" aria-label="On this page">
      <p className="cs-toc-label">On this page</p>
      <nav>
        {items.map((item) => (
          <a
            key={item.id}
            href={`#${item.id}`}
            className={`cs-toc-link${activeId === item.id ? " is-active" : ""}`}
            onClick={(event) => {
              event.preventDefault();
              setActiveId(item.id);
              scrollToSection(item.id);
              window.history.replaceState(null, "", `#${item.id}`);
            }}
          >
            {item.label}
          </a>
        ))}
      </nav>
    </aside>
  );
};

export default CaseStudyToc;
